
import wrapper from 'wrapper';
import d3 from './d3-imports';
import events from '../scrolly-library/events-emitter';
import data from '../../assets/data/locusts-chart';
import visualisationConfig from './visualisation-config';
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */

export default class Visualisation {
    constructor() {
        this.data = data;
        this.scale = window.devicePixelRatio;
        this.pointWidth = 12 * this.scale;
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.animated = wrapper.select('.animation-toggle input').checked;
        this.vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        this.breakpoint = visualisationConfig.breakpoint;

        this.allLocusts = (this.vw <= this.breakpoint) ? 5000 : 10000;
        this.someLocusts = 20;
        this.duration = 3000; // 1500ms = 1.5s

        this.layouts = {
            smallNumberOfLocusts: () => this.smallNumberOfLocusts(),
            allTheLocusts: () => this.allTheLocusts(),
            smallNumberOfLocustsFlyAway: () => this.smallNumberOfLocustsFlyAway(),
            chartOfLocusts: () => this.chartOfLocusts(),
        };
        this.currentLayout = 0;
        this.chartIndex = 3;
        this.rng = d3.randomNormal(0, 0.05);

        this.initialPoints = this.createLocusts(this.someLocusts);
        this.allPoints = this.createLocusts(this.allLocusts);
        events.on('animation-toggled', (checked) => {
            this.animated = checked;
        });
        events.on('resize', () => {
            this.vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
            this.updateMargins(this.vw);
        });
        this.setUpChartVariables();

        window.addEventListener('blur', () => {
            this.stopAnimationLoop();
        });

        window.addEventListener('focus', () => {
            this.update(this.layoutName);
        });
    }

    setUpChartVariables() {
        const format = this.formatData();
        this.formattedData = format.data;
        this.totalDataCount = format.totalCount;
        this.years = Object.keys(this.formattedData);
        this.marginGraph = {
            left: (this.vw <= this.breakpoint) ? 50 : 100,
            top: wrapper.select('.chart-title-container').clientHeight + 50,
            right: (this.vw <= this.breakpoint) ? 50 : 100,
            bottom: 0,
        };
        this.widthChart = wrapper.select('.chart').clientWidth;
        this.heightChart = wrapper.select('.chart').clientHeight - wrapper.select('.chart-title-container').clientHeight - wrapper.select('.chart-note-container').clientHeight;
        this.widthGraph = this.widthChart - this.marginGraph.left - this.marginGraph.right;
        this.heightGraph = this.heightChart - this.marginGraph.top - this.marginGraph.bottom;
        this.scaleX = d3.scaleLinear().domain([0, 50]).range([0, this.widthGraph]);
        this.scaleY = d3.scaleLinear().domain([95, 0]).range([0, this.heightGraph]);
        this.dataTypes = visualisationConfig.dataTypes;
    }

    formatData() {
        const formattedData = {};
        let totalCount = 0;
        this.data.forEach((datum) => {
            formattedData[datum.year] = datum;
            totalCount += datum.recession + datum.upsurge + datum.plague;
        });
        return { data: formattedData, totalCount };
    }

    init(err, regl) {
        this.regl = regl;
        this.points = this.initialPoints;
        events.emit('createChart');
    }

    update(layoutName) {
        this.layoutName = layoutName;
        const index = Object.keys(this.layouts).indexOf(layoutName);
        this.currentLayout = index;
        this.points = (index > 0) ? [...this.points, ...this.allPoints] : this.initialPoints;
        this.stopAnimationLoop();
        this.animate(this.layouts[layoutName]);
    }

    stopAnimationLoop() {
        if (this.frameLoop) {
            this.frameLoop.cancel();
            this.frameLoop = null;
        }
    }

    smallNumberOfLocusts() {
        this.duration = 3000;
        this.points.forEach((d) => {
            d.x = this.randomPositionCentreScreen('x');
            d.y = this.randomPositionCentreScreen('y');
            d.color = this.randomLocustColour();
        });
    }

    smallNumberOfLocustsFlyAway() {
        this.duration = 3000;
        this.points.forEach((d, i) => {
            d.x = (i <= this.someLocusts) ? this.randomPositionCentreScreen('x') : this.randomPositionOffScreen('x', i);
            d.y = (i <= this.someLocusts) ? this.randomPositionCentreScreen('y') : this.randomPositionOffScreen('y', i);
            d.color = d.color;
        });
    }

    allTheLocusts() {
        this.duration = 3000;
        const rng = d3.randomNormal(0, 0.5);
        this.points.forEach((d, i) => {
            d.x = rng() * Math.cos(i) * this.width + this.width / 2;
            d.y = rng() * Math.cos(i) * this.height + this.height / 2;
            d.color = this.randomLocustColour();
        });
    }

    randomLocustColour() {
        return visualisationConfig.coloursForRegl[this.dataTypes[Math.floor(Math.random() * 3)]];
    }

    randomPositionOffScreen(position, i) {
        const coordinates = {
            x: (this.rng() + Math.cos(i)) * (this.width) + this.width / 2,
            y: (this.rng() + Math.sin(i)) * (this.height) + this.height / 2,
        };
        return coordinates[position];
    }

    randomPositionCentreScreen(position) {
        const coordinates = {
            x: this.rng() * this.width + this.width / 2,
            y: this.rng() * this.height + this.height / 2,
        };
        return coordinates[position];
    }

    chartOfLocusts() {
        this.duration = 3000;

        // move extra dots off screen
        const rng = d3.randomNormal(0, 0.5);
        this.points.forEach((d, i) => {
            if (i >= this.totalDataCount) {
                d.x = this.width * 2;
                d.y = rng() * Math.cos(i) * this.height + this.height / 2;
            }
        });

        // move dots into chart shape
        this.index = 0;
        this.years.forEach((year, j) => {
            this.dataTypes.forEach((category) => {
                if (this.formattedData[year][category]) {
                    for (let i = 0; i < this.formattedData[year][category]; i++) {
                        this.updatePoint(j, i, visualisationConfig.coloursForRegl[category]);
                    }
                }
                this.index += 1;
            });
        });

        events.emit('showChart');
    }

    updatePoint(yIndex, xIndex, colour) {
        this.points[this.index]['x'] = this.scaleX(xIndex) + this.marginGraph.left;
        this.points[this.index]['y'] = this.scaleY(yIndex) + this.marginGraph.top;
        this.points[this.index]['color'] = colour;
        this.index += 1;
    }

    randomMovementOfLocusts(size) {
        const conf = {
            small: [200, 10],
            large: [100, 20],
        };
        const [duration, standardDeviation] = conf[size];
        this.duration = duration;
        this.points.forEach((d) => {
            d.x = d3.randomNormal(d.x, standardDeviation)();
            d.y = d3.randomNormal(d.y, standardDeviation)();
            d.color = d.color;
        });
    }

    createLocusts(locustNumber) {
        return d3.range(locustNumber).map(i => ({
            tx: this.randomPositionOffScreen('x', i),
            ty: this.randomPositionOffScreen('y', i),
            colorEnd: [0, 0, 0],
        }));
    }

    createDrawPoints(points) {
        const drawPoints = this.regl({
            frag: `
      // set the precision of floating point numbers
      precision highp float;
      // this value is populated by the vertex shader
      varying vec3 fragColor;
      void main() {
        float r = 0.0, delta = 0.0, alpha = 1.0;
        vec2 cxy = 2.0 * gl_PointCoord - 1.0;
        r = dot(cxy, cxy);
        if (r > 1.0) {
            discard;
        }
        gl_FragColor = vec4(fragColor, 1);
      }
      `,

            vert: `
      // per vertex attributes
      attribute vec2 positionStart;
      attribute vec2 positionEnd;
      attribute vec3 colorStart;
      attribute vec3 colorEnd;
      // variables to send to the fragment shader
      varying vec3 fragColor;
      // values that are the same for all vertices
      uniform float pointWidth;
      uniform float stageWidth;
      uniform float stageHeight;
      uniform float elapsed;
      uniform float duration;
      // helper function to transform from pixel space to normalized device coordinates (NDC)
      // in NDC (0,0) is the middle, (-1, 1) is the top left and (1, -1) is the bottom right.
      vec2 normalizeCoords(vec2 position) {
        // read in the positions into x and y vars
        float x = position[0];
        float y = position[1];
        return vec2(
          2.0 * ((x / stageWidth) - 0.5),
          // invert y since we think [0,0] is bottom left in pixel space
          -(2.0 * ((y / stageHeight) - 0.5)));
      }
      // helper function to handle cubic easing (copied from d3 for consistency)
      // note there are pre-made easing functions available via glslify.
      float easeCubicInOut(float t) {
        t *= 2.0;
        t = (t <= 1.0 ? t * t * t : (t -= 2.0) * t * t + 2.0) / 2.0;
        if (t > 1.0) {
          t = 1.0;
        }
        return t;
      }
      void main() {
        // update the size of a point based on the prop pointWidth
        gl_PointSize = pointWidth;
        // number between 0 and 1 indicating how far through the animation this
        // vertex is.
        float t;
        // drawing without animation, so show end state immediately
        if (duration == 0.0) {
          t = 1.0;
        // otherwise we are animating, so use cubic easing
        } else {
          t = elapsed / duration;
        }
        // interpolate position
        vec2 position = mix(positionStart, positionEnd, t);
        // interpolate and send color to the fragment shader
        fragColor = mix(colorStart, colorEnd, t);
        // scale to normalized device coordinates
        // gl_Position is a special variable that holds the position of a vertex
        gl_Position = vec4(normalizeCoords(position), 0.0, 1.0);
      }
      `,

            attributes: {
                // each of these gets mapped to a single entry for each of the points.
                // this means the vertex shader will receive just the relevant value for a given point.
                positionStart: points.map(d => [d.sx, d.sy]),
                positionEnd: points.map(d => [d.tx, d.ty]),
                colorStart: points.map(d => d.colorStart),
                colorEnd: points.map(d => d.colorEnd),
            },

            uniforms: {
                // by using `regl.prop` to pass these in, we can specify them as arguments
                // to our drawPoints function
                pointWidth: this.regl.prop('pointWidth'),

                // regl actually provides these as viewportWidth and viewportHeight but I
                // am using these outside and I want to ensure they are the same numbers,
                // so I am explicitly passing them in.
                stageWidth: this.regl.prop('stageWidth'),
                stageHeight: this.regl.prop('stageHeight'),

                duration: this.regl.prop('duration'),
                // time in milliseconds since the prop startTime (i.e. time elapsed)
                // note that `time` is passed by regl whereas `startTime` is a prop passed
                // to the drawPoints function.
                elapsed: ({
                    time,
                }, {
                    startTime = 0,
                }) => (time - startTime) * 1000,
            },

            // specify the number of points to draw
            count: points.length,

            // specify that each vertex is a point (not part of a mesh)
            primitive: 'points',
        });

        return drawPoints;
    }

    animate(layout) {
        this.points.forEach(d => {
            d.sx = d.tx;
            d.sy = d.ty;
            d.colorStart = d.colorEnd;
        });

        // this is a callback calls specified layout function
        layout(this.points);

        this.points.forEach((d) => {
            d.tx = d.x;
            d.ty = d.y;
            d.colorEnd = d.color;
        });

        const drawPoints = this.createDrawPoints(this.points);

        let startTime = null; // in seconds
        this.frameLoop = this.regl.frame(({
            time,
        }) => {
            if (startTime === null) {
                startTime = time;
            }

            this.regl.clear({
                color: [0, 0, 0, 0],
                depth: 1,
            });

            drawPoints({
                pointWidth: this.pointWidth,
                stageWidth: this.width,
                stageHeight: this.height,
                duration: this.animated ? this.duration : 0,
                startTime,
            });

            if (time - startTime > this.duration / 1000 && this.animated) {
                this.stopAnimationLoop();
                if (this.currentLayout < 2) this.animate(() => this.randomMovementOfLocusts(this.points.length > 1000 ? 'large' : 'small'));
                if (this.currentLayout === 2) this.animate(() => this.randomMovementOfLocusts('small'));
            }
        });
    }
}
